import { pb } from './pocketbase';
import { createNotification } from './notification.service';

// Rechercher des étudiants par nom, email ou téléphone
export const searchStudents = async (query) => {
    try {
        if (!query || query.length < 2) {
            return [];
        }
        
        const result = await pb.collection('students').getList(1, 10, {
            filter: `first_name ~ "${query}" || last_name ~ "${query}" || email ~ "${query}" || phone ~ "${query}"`,
            expand: 'formation' 
        });
        
        return result.items;
    } catch (error) {
        console.error('Erreur lors de la recherche des étudiants:', error);
        return [];
    }
};

// Récupérer tous les étudiants d'une promotion
export const getStudentsByPromotion = async (promotionId) => {
    try {
        if (!promotionId) {
            return [];
        }
        
        const result = await pb.collection('students').getFullList({
            filter: `promotion_id = "${promotionId}"`,
            sort: 'first_name,last_name',
            expand: 'formation'
        });
        
        return result;
    } catch (error) {
        console.error('Erreur lors de la récupération des étudiants de la promotion:', error);
        return [];
    }
};

export const getStudentById = async (recordId, options = {}) => {
    try {
        const record = await pb.collection('students').getOne(recordId, options);
        return record;
    } catch (error) {
        console.error('Erreur lors de la récupération de l\'étudiant:', error);
        throw error;
    }
};

export const getStudentList = async (options = {}) => {
    try {
        // Préparation des options de requête avec expansion de la relation formation
        const queryOptions = {
            expand: 'formation, company_id',  // Expansion automatique de la relation formation
            sort: options.sort || '-created',
            filter: options.filter || '',
            page: options.page || 1,
            perPage: options.perPage || 20,
            fullData: options.fullData || false
        };
        
        // Préparation des options pour PocketBase
        const pbOptions = {
            expand: queryOptions.expand,
            sort: queryOptions.sort,
            filter: queryOptions.filter
        };
        
        if (!queryOptions.fullData) {
            pbOptions.fields = options.fields || 'id,first_name,last_name,email,phone,type,inscription_at,status,expand.formation.exact_title_of_qualification,expand.company_id.name,file_status';
        }
        
        // Récupération des étudiants avec leurs formations en une seule requête
        const result = await pb.collection('students').getList(
            queryOptions.page,
            queryOptions.perPage,
            pbOptions
        );
        
        // Retourner les résultats déjà formatés par PocketBase
        return {
            items: result.items,
            totalItems: result.totalItems,
            totalPages: result.totalPages,
            page: result.page,
            perPage: queryOptions.perPage
        };
    } catch (error) {
        console.error('Erreur lors de la récupération de la liste des étudiants:', error);
        
        // En cas d'erreur, retourner une liste vide dans le format attendu
        return {
            items: [],
            totalItems: 0,
            page: 1,
            perPage: 20,
            totalPages: 0
        };
    }
};

export const updateStudent = async (recordId, data) => {
    try {
       
        if (pb.authStore.isValid && pb.authStore.model) {
            const updatedStudent = await pb.collection('students').update(recordId, data);
            return updatedStudent;
        } else {
            console.error('Aucun utilisateur authentifié. Impossible de mettre à jour l\'étudiant sans partner_id.');
            return null;
        }    
    } catch (error) {
        console.error('Erreur lors de la mise à jour de l\'étudiant:', error);
        throw error;
    }
};

export const updateStudentFileStatus = async (studentId, fileStatus) => {
    try {
        if (!studentId) {
            throw new Error('ID étudiant non fourni');
        }
        
        // Valider le statut de fichier
        if (!['Complet', 'Incomplet'].includes(fileStatus)) {
            throw new Error('Statut de fichier invalide. Utilisez "Complet" ou "Incomplet"');
        }
                
        // Mettre à jour uniquement le statut du dossier sans modifier les autres champs
        const updatedStudent = await pb.collection('students').update(studentId, {
            file_status: fileStatus
        });
        
        // Retourner l'étudiant mis à jour avec toutes ses informations
        return updatedStudent;
    } catch (error) {
        console.error('Erreur lors de la mise à jour du statut de dossier:', error);
        throw error;
    }
};

export const getStudentForCurrentUser = async () => {
    try {
        // Vérifier si l'utilisateur est connecté
        if (!pb.authStore.isValid || !pb.authStore.model) {
            throw new Error('Utilisateur non connecté');
        }
        
        const authData = pb.authStore.model;
        
        // Vérifier si l'utilisateur a un student_id
        if (!authData.student_id) {
            throw new Error('Aucun étudiant associé à cet utilisateur');
        }
        
        // Récupérer directement l'étudiant par son ID
        const student = await pb.collection('students').getOne(authData.student_id);
        
        return student;
    } catch (error) {
        console.error('Erreur lors de la récupération de l\'étudiant:', error);
        throw error;
    }
};

export const notificationFolderIncomplete = async (userRef) => {
    try {
        await createNotification({
            type: 'folder_incomplete',
            title: 'Compléter votre dossier',
            message: 'Votre dossier est incomplet. Veuillez le compléter pour pouvoir accéder à vos formations.',
            icon: 'contract_logo.png',
            recipient_id: userRef,
            link: '/student/account',
            read: false
        });
        
        return { success: true };
    } catch (error) {
        console.error('Erreur lors de l\'envoi de la notification de relance:', error);
        throw error;
    }
};

export const searchProspectsForOffer = async (query = '') => {
    try {
      const consentFilter = `consent_to_share_with_companies = true`;
      const companyFilter = `company = null`; 
      
      let filter = `${consentFilter} && ${companyFilter}`;
      
      if (query && query.trim().length >= 2) {
        const searchFilter = `(first_name ~ "${query}" || last_name ~ "${query}" || email ~ "${query}" || phone ~ "${query}")`;
        filter = `${filter} && ${searchFilter}`;
      }
  
      const result = await pb.collection('students').getList(1, 50, {
        filter,
        sort: '-created' 
      });
      
      console.log(`Prospects trouvés avec consentement et sans entreprise:`, result.items?.length || 0);
      return result.items;
    } catch (error) {
      console.error('Erreur lors de la recherche des prospects pour offre:', error);
      return [];
    }
  };

export const sendStudentCredentials = async (studentId) => {
    try {
        // Récupérer l'étudiant pour obtenir son email
        const student = await pb.collection('students').getOne(studentId);
        
        if (!student || !student.email) {
            throw new Error('Email de l\'étudiant non trouvé');
        }
        
        // Utiliser la même mécanique que RequestPasswordReset mais avec l'email de l'étudiant
        await pb.collection('users').requestPasswordReset(student.email, {
            emailTemplate: 'password_reset',
            // URL de réinitialisation pointant vers le frontend
            actionUrl: `${window.location.origin}/reset-password?token={TOKEN}`
        });
        
        return { success: true, message: 'Email de réinitialisation envoyé avec succès' };
    } catch (error) {
        console.error('Erreur lors de l\'envoi des identifiants:', error);
        throw error;
    }
};
